import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, QrCode, History as HistoryIcon, BookOpen, Layers } from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Generator", icon: QrCode },
  { to: "/history", label: "Verlauf", icon: HistoryIcon },
  { to: "/docs", label: "API", icon: BookOpen },
];

export default function Layout({ children, hideNav = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const initials = (user?.name || user?.email || "M")
    .split(/\s+/)
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="min-h-screen bg-[#F4F4F5] text-zinc-900">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white/85 backdrop-blur" data-testid="app-header">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 md:px-8 py-3">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 group" data-testid="app-logo-link">
            <span className="grid place-items-center w-8 h-8 rounded-md bg-[#0A52FF] text-white">
              <Layers className="w-4 h-4" />
            </span>
            <div className="leading-tight">
              <div className="font-display font-extrabold tracking-tight text-[15px]">Makrolog AG</div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">QR Code Studio</div>
            </div>
          </Link>

          {!hideNav && user && (
            <nav className="hidden md:flex items-center gap-1" data-testid="primary-nav">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-3">
            {!user ? (
              <Button
                variant="default"
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
                className="bg-[#0A52FF] hover:bg-[#003ECC]"
              >
                Anmelden
              </Button>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="flex items-center gap-2 rounded-full pr-2 pl-1 py-1 hover:bg-zinc-100 transition-colors"
                    data-testid="user-menu-trigger"
                  >
                    {user.picture ? (
                      <img
                        src={user.picture}
                        alt={user.name || user.email}
                        className="w-8 h-8 rounded-full border border-zinc-200"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-full bg-zinc-900 text-white grid place-items-center text-xs font-bold">
                        {initials}
                      </span>
                    )}
                    <span className="hidden sm:inline text-sm font-medium text-zinc-800 max-w-[160px] truncate">
                      {user.name || user.email}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56" data-testid="user-menu-content">
                  <DropdownMenuLabel className="text-xs">
                    <div className="font-semibold truncate">{user.name}</div>
                    <div className="text-zinc-500 truncate font-normal">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-go-dashboard">
                    <QrCode className="w-4 h-4 mr-2" /> Generator
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/history")} data-testid="menu-go-history">
                    <HistoryIcon className="w-4 h-4 mr-2" /> Verlauf
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={async () => {
                      await logout();
                      navigate("/login");
                    }}
                    data-testid="menu-logout"
                    className="text-red-600 focus:text-red-700"
                  >
                    <LogOut className="w-4 h-4 mr-2" /> Abmelden
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {!hideNav && user && (
          <div className="md:hidden border-t border-zinc-200 bg-white">
            <div className="mx-auto max-w-7xl px-4 py-2 flex gap-1 overflow-x-auto" data-testid="mobile-nav">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-1.5 rounded-md text-sm whitespace-nowrap ${
                      isActive ? "bg-zinc-900 text-white" : "text-zinc-700 hover:bg-zinc-100"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" />
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main key={location.pathname} className="fade-in">{children}</main>

      <footer className="border-t border-zinc-200 bg-white mt-12">
        <div className="mx-auto max-w-7xl px-4 md:px-8 py-6 text-xs text-zinc-500 flex flex-wrap items-center justify-between gap-2">
          <span>© {new Date().getFullYear()} Makrolog AG · QR Code Studio</span>
          <span className="font-mono">qr.makrolog.page</span>
        </div>
      </footer>
    </div>
  );
}
